'use client';

import React from 'react';
import {
  Box,
  InputAdornment,
  Stack,
  Typography,
  MenuItem,
  Link,
} from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { MuiTextField } from '@/components/textfield/MuiTextField';
import { RHFSelect } from '@/components/select/RHFSelect';
import { MuiButton } from '@/components/button/MuiButton';
import { RHFRadioGroup } from '@/components/radio/RHFRadioGroup';
import { CalendarFilterField } from './CalendarFilterField';
import { PREFECTURES } from '@/data/prefectures';
import { SearchStationDetailType } from '@/schemas/SearchStationDetailSchema';
import {
  MOVING_MINUTES,
  getSearchTypeLabel,
  STATION_ADVANCED_SEARCH_TYPES,
} from '@/types/types';
import { useLogic } from './logic';
import { RadioLabel } from '@/components/radio/RadioLabel';
import { SearchTargetSwitch } from '@/features/hospital/HospitalsSearchBox/SearchTargetSwitch';
import { PublicCalendarFilterField } from './PublicCalendarFilterField';

type Props = {
  formMethod: UseFormReturn<SearchStationDetailType>;
  isPublicSearch?: boolean;
};

export function SearchBox({ formMethod, isPublicSearch = false }: Props) {
  const { searchType } = useLogic({ formMethod });

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Typography
          sx={{
            typography: {
              xs: 'JP/Heading/s/Bold/Tight',
              sm: 'JP/Heading/s/Bold/Tight',
            },
          }}
        >
          看護・介護サービスを探す
        </Typography>

        <SearchTargetSwitch type={'station'} isPublicSearch={isPublicSearch} />
      </Box>

      <Box
        sx={{
          marginTop: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <RHFRadioGroup
          aria-labelledby={`radiogroup-search-type`}
          name={formMethod.register('searchType').name}
          control={formMethod.control}
          defaultValue={searchType}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {Object.values(STATION_ADVANCED_SEARCH_TYPES).map((type) => (
              <Box key={`search-type-${type}`}>
                <RadioLabel value={type} label={getSearchTypeLabel(type)} />
              </Box>
            ))}
          </Box>
        </RHFRadioGroup>

        <Link
          href={`/${isPublicSearch ? 'public' : 'app'}/stations/search/word`}
          color="#13131D"
          underline="none"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="end"
            sx={{
              typography: {
                xs: 'JP/Body/s/Medium/None',
                sm: 'JP/Body/s/Medium/None',
              },
            }}
          >
            看護・介護サービス名から検索はこちら
            <NavigateNextIcon fontSize="small" />
          </Stack>
        </Link>
      </Box>

      <Box
        sx={{
          marginTop: 1,
          display: 'flex',
          alignItems: 'center',
          '@media (max-width: 800px)': {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
        }}
      >
        {searchType === STATION_ADVANCED_SEARCH_TYPES.ZIPCODE && (
          <>
            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 1,
                  gridColumn: '1 / span 2',
                },
              }}
            >
              <MuiTextField
                name={formMethod.register('location.zipcode').name}
                control={formMethod.control}
                placeholder="郵便番号（例:1110000）"
                fullWidth
                size="small"
                sx={{ backgroundColor: '#FAFAFA' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">〒</InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 2,
                  gridColumn: 1,
                },
              }}
            >
              {isPublicSearch ? (
                <PublicCalendarFilterField />
              ) : (
                <CalendarFilterField
                  formMethod={formMethod}
                  size="small"
                  sx={{ backgroundColor: '#FAFAFA' }}
                />
              )}
            </Box>
          </>
        )}

        {searchType === STATION_ADVANCED_SEARCH_TYPES.ADDRESS && (
          <>
            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 1,
                  gridColumn: 1,
                },
              }}
            >
              <RHFSelect
                name={formMethod.register('location.pref_name').name}
                control={formMethod.control}
                sx={{ backgroundColor: '#FAFAFA', height: '40px' }}
                placeholder="都道府県"
                variant="outlined"
              >
                {PREFECTURES.map((pref) => {
                  return (
                    <MenuItem key={pref.value} value={pref.text}>
                      {pref.text}
                    </MenuItem>
                  );
                })}
              </RHFSelect>
            </Box>

            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 1,
                  gridColumn: 2,
                },
              }}
            >
              <MuiTextField
                name={formMethod.register('location.address').name}
                control={formMethod.control}
                placeholder="都道府県以降の住所を入力"
                fullWidth
                size="small"
                sx={{ backgroundColor: '#FAFAFA' }}
              />
            </Box>

            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 2,
                  gridColumn: 1,
                },
              }}
            >
              {isPublicSearch ? (
                <PublicCalendarFilterField />
              ) : (
                <CalendarFilterField
                  formMethod={formMethod}
                  size="small"
                  sx={{ backgroundColor: '#FAFAFA' }}
                />
              )}
            </Box>
          </>
        )}

        {searchType === STATION_ADVANCED_SEARCH_TYPES.PARTNER && (
          <>
            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 1,
                  gridColumn: 1,
                },
              }}
            >
              <RHFSelect
                name={formMethod.register('location.pref_name').name}
                control={formMethod.control}
                sx={{ backgroundColor: '#FAFAFA', height: '40px' }}
                placeholder="都道府県"
                variant="outlined"
              >
                {PREFECTURES.map((pref) => {
                  return (
                    <MenuItem key={pref.value} value={pref.text}>
                      {pref.text}
                    </MenuItem>
                  );
                })}
              </RHFSelect>
            </Box>

            <Box
              sx={{
                width: 1,
                '@media (max-width: 800px)': {
                  gridRow: 1,
                  gridColumn: 2,
                },
              }}
            >
              <MuiTextField
                name={formMethod.register('partnerName').name}
                control={formMethod.control}
                placeholder="地域連携先"
                fullWidth
                size="small"
                sx={{ backgroundColor: '#FAFAFA' }}
              />
            </Box>
          </>
        )}

        <Box
          sx={{
            width: 1,
            '@media (max-width: 800px)': {
              gridRow: 2,
              gridColumn: 2,
            },
          }}
        >
          <RHFSelect
            name={formMethod.register('location.moving_minutes').name}
            control={formMethod.control}
            sx={{ backgroundColor: '#FAFAFA', height: '40px' }}
            variant="outlined"
          >
            {MOVING_MINUTES.map((mm) => {
              return (
                <MenuItem key={mm.value} value={mm.value}>
                  {mm.text}
                </MenuItem>
              );
            })}
          </RHFSelect>
        </Box>

        <Box
          sx={{
            '@media (max-width: 800px)': {
              gridRow: 3,
              gridColumn: '1 / span 2',
            },
          }}
        >
          <MuiButton
            sx={{
              width: '64px',
              height: '40px',
              ml: 1,
              mt: 0,
              background: '#33BEB9',
              '@media (max-width: 800px)': {
                width: '100%',
                height: '40px',
                ml: 0,
                mt: 1,
                background: '#33BEB9',
              },
            }}
            type="submit"
            disabled={
              // NOTE: 郵便番号が空の状態で検索ボタンを押されないようにする
              //      可能なら、郵便番号のバリデーションや住所検索でのバリデーションも追加したい
              formMethod.watch('searchType') ===
                STATION_ADVANCED_SEARCH_TYPES.ZIPCODE &&
              formMethod.watch('location.zipcode') === ''
            }
          >
            検索
          </MuiButton>
        </Box>
      </Box>
    </>
  );
}
